import { Fragment, useEffect, useState } from 'react';
import { cn } from '../../../../../../lib/utils';
import { primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../../styling/stylingHelper';
import {
  getAICheckerMissingFiles,
  getAICheckerMissingFileEmails,
  attachAICheckerMissingFiles,
  type MissingSourceFileStats,
  type MissingSourceFileEmail,
  type AttachMissingFilesResult,
} from '../../../../../../services/admin/adminService';
import type { TFn } from '../../types';

/**
 * The affected emails under one supplier. Each can be dry-run or repaired on its own — the escape
 * hatch for bundles the bulk run reports as partly unmatched, where you want to see the outcome for
 * one email before trusting the rest.
 */
function MissingFileEmailList({ platform, startDate, endDate, userId, listLimit, onRepaired, t }: { platform: string; startDate: string; endDate: string; userId?: string; listLimit: number; onRepaired: () => void; t: TFn }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emails, setEmails] = useState<MissingSourceFileEmail[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const r = await getAICheckerMissingFileEmails(startDate, endDate, platform, listLimit, userId);
        if (!cancelled) setEmails(r);
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to list affected emails');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [platform, startDate, endDate, userId, listLimit]);

  const runOne = async (email: MissingSourceFileEmail, dryRun: boolean) => {
    setBusyId(email.gmailMessageId);
    try {
      const r = await attachAICheckerMissingFiles({
        account: email.account || 'all',
        startDate,
        endDate,
        platform,
        emailLimit: 1,
        dryRun,
        gmailMessageIds: [email.gmailMessageId],
      });
      const detail = r.emails[0];
      setOutcomes((cur) => ({
        ...cur,
        [email.gmailMessageId]: detail
          ? `${dryRun ? t('adminTesting.review.missingFilesWould') : t('adminTesting.review.missingFilesDid')} ${t('adminTesting.review.missingFilesLinkedCounts', { orders: detail.ordersLinked, ordersTotal: email.missingOrders, facturen: detail.facturenLinked })} · ${detail.pdfCount} pdf · ${t(`adminTesting.review.missingFilesOutcome_${detail.outcome}`)}${detail.unmatchedOrderNumbers.length ? ` · ${t('adminTesting.review.missingFilesUnmatchedShort')}: ${detail.unmatchedOrderNumbers.join(', ')}` : ''}`
          : t('adminTesting.review.missingFilesOutcome_email_not_found'),
      }));
      if (!dryRun) onRepaired();
    } catch (e: any) {
      setOutcomes((cur) => ({ ...cur, [email.gmailMessageId]: e.message || 'failed' }));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <div className={cn(secondaryText, 'text-[11px] py-2 px-6')}>{t('adminTesting.review.missingFilesLoading')}</div>;
  if (error) return <div className={cn('text-red-500 dark:text-textRed-dark text-[11px] py-2 px-6')}>{error}</div>;
  if (emails.length === 0) return <div className={cn(secondaryText, 'text-[11px] py-2 px-6')}>{t('adminTesting.review.missingFilesEmpty')}</div>;

  return (
    <div className="bg-gray-100/60 dark:bg-gray-900/30 px-6 py-2 space-y-1">
      {emails.map((e) => (
        <div key={e.gmailMessageId} className="flex flex-wrap items-center gap-2 text-[11px]">
          <span className={cn(secondaryText, 'font-mono w-20 shrink-0')}>
            {e.emailDate ? new Date(e.emailDate).toLocaleDateString() : '—'}
          </span>
          <span className={cn(primaryText, 'flex-1 min-w-[10rem] truncate')} title={e.subject || e.gmailMessageId}>
            {e.subject || e.gmailMessageId}
          </span>
          <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap',
            e.shape === 'bundle'
              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
              : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300')}>
            {t(`adminTesting.review.missingFilesShape_${e.shape}`)}
          </span>
          <span className={cn('font-mono whitespace-nowrap', 'text-red-600 dark:text-red-400')}>
            {t('adminTesting.review.missingFilesNMissing', { count: e.missingOrders })}
          </span>
          {e.missingOrdersInWindow !== e.missingOrders && (
            <span className={cn(secondaryText, 'font-mono text-[10px] whitespace-nowrap')} title={t('adminTesting.review.missingFilesOutsideWindowHint')}>
              {t('adminTesting.review.missingFilesInWindow', { count: e.missingOrdersInWindow })}
            </span>
          )}
          {e.filesAlreadyOnEmail > 0 && (
            <span className={cn(secondaryText, 'font-mono text-[10px] whitespace-nowrap')} title={t('adminTesting.review.missingFilesPartialHint')}>
              {t('adminTesting.review.missingFilesPartial', { count: e.filesAlreadyOnEmail })}
            </span>
          )}
          <button
            onClick={() => runOne(e, true)}
            disabled={busyId === e.gmailMessageId}
            className={cn('px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50', primaryText)}
          >
            {t('adminTesting.review.missingFilesDryRun')}
          </button>
          <button
            onClick={() => runOne(e, false)}
            disabled={busyId === e.gmailMessageId}
            className="px-2 py-0.5 rounded bg-purple-600 dark:bg-purple-600 text-white dark:text-white hover:bg-purple-700 dark:hover:bg-purple-700 disabled:opacity-50"
          >
            {busyId === e.gmailMessageId ? '…' : t('adminTesting.review.missingFilesFix')}
          </button>
          {outcomes[e.gmailMessageId] && (
            <span className={cn(secondaryText, 'font-mono text-[10px] w-full pl-20')}>{outcomes[e.gmailMessageId]}</span>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Source-file coverage: which stored orders/facturen have no PDF linked, and the repair run that
 * attaches them. The gap comes from every path that isn't "one email → one file → new rows":
 * backprocessed bundles, dedup skips, failed uploads, rows predating file storage.
 *
 * The repair works one batch of emails at a time (each costs a Gmail fetch + PDF text extraction)
 * and is idempotent, so "Attach files" is pressed repeatedly until the missing count stops moving.
 */
export function AICheckerMissingFiles({ startDate, endDate, onCount, t }: { startDate: string; endDate: string; onCount: (n: number) => void; t: TFn }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<MissingSourceFileStats | null>(null);
  const [platform, setPlatform] = useState('all');
  const [running, setRunning] = useState(false);
  const [run, setRun] = useState<AttachMissingFilesResult | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [clientId, setClientId] = useState('all');
  // Emails per run. Free-form: each one costs a Gmail fetch + PDF extraction, so the useful size
  // depends on how far you trust the matcher — small while verifying, large once you don't.
  const [batchSize, setBatchSize] = useState(25);
  const toggleExpanded = (p: string) => setExpanded((cur) => (cur === p ? null : p));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const r = await getAICheckerMissingFiles(startDate, endDate, 'all', clientId === 'all' ? undefined : clientId);
        if (!cancelled) { setStats(r); onCount(r.missingOrders); }
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to load missing source files');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [startDate, endDate, onCount, reloadNonce, clientId]);

  const attach = async (dryRun: boolean) => {
    setRunning(true);
    setError(null);
    try {
      const r = await attachAICheckerMissingFiles({
        account: 'all', startDate, endDate, platform,
        userId: clientId === 'all' ? undefined : clientId,
        emailLimit: batchSize, dryRun,
      });
      setRun(r);
      // A real run changed the numbers the table above shows — re-read them.
      if (!dryRun) setReloadNonce((n) => n + 1);
    } catch (e: any) {
      setError(e.message || 'Failed to attach missing files');
    } finally {
      setRunning(false);
    }
  };

  if (loading && !stats) return <div className={cn(secondaryText, smallTextSize, 'text-center py-8')}>{t('adminTesting.review.missingFilesLoading')}</div>;
  if (error && !stats) return <div className={cn('text-center py-8 text-red-500 dark:text-textRed-dark', smallTextSize)}>{error}</div>;
  if (!stats) return null;

  const coveragePct = stats.totalOrders > 0
    ? Math.round(((stats.totalOrders - stats.missingOrders) / stats.totalOrders) * 100)
    : 100;
  const platformsWithGap = stats.byPlatform.filter((r) => r.missingOrders > 0 || r.missingFacturen > 0);

  return (
    <div className="space-y-4">
      <p className={cn(secondaryText, 'text-[11px] italic')}>{t('adminTesting.review.missingFilesCaption')}</p>

      <div className="flex flex-wrap items-center gap-4">
        <div>
          <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide')}>{t('adminTesting.review.missingFilesCoverage')}</div>
          <div className={cn(primaryText, 'text-2xl font-semibold')}>{coveragePct}%</div>
        </div>
        <div>
          <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide')}>{t('adminTesting.review.missingFilesOrders')}</div>
          <div className={cn('text-2xl font-semibold', stats.missingOrders > 0 ? 'text-red-600 dark:text-red-400' : primaryText)}>{stats.missingOrders}</div>
          <div className={cn(secondaryText, 'text-[10px] font-mono')}>{t('adminTesting.review.missingFilesOfTotal', { total: stats.totalOrders })}</div>
        </div>
        <div>
          <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide')}>{t('adminTesting.review.missingFilesFacturen')}</div>
          <div className={cn('text-2xl font-semibold', stats.missingFacturen > 0 ? 'text-red-600 dark:text-red-400' : primaryText)}>{stats.missingFacturen}</div>
          <div className={cn(secondaryText, 'text-[10px] font-mono')}>{t('adminTesting.review.missingFilesOfTotal', { total: stats.totalFacturen })}</div>
        </div>
        <div>
          <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide')}>{t('adminTesting.review.missingFilesEmails')}</div>
          <div className={cn(primaryText, 'text-2xl font-semibold')}>{stats.missingEmails}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select
          value={clientId}
          onChange={(e) => { setClientId(e.target.value); setExpanded(null); }}
          className={cn('px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700', primaryText, smallTextSize)}
        >
          <option value="all">{t('adminTesting.review.missingFilesAllClients')}</option>
          {stats.byClient.map((c) => (
            <option key={c.userId} value={c.userId}>{c.clientName} ({c.missingOrders})</option>
          ))}
        </select>
        <select
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
          className={cn('px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700', primaryText, smallTextSize)}
        >
          <option value="all">{t('adminTesting.review.missingFilesAllPlatforms')}</option>
          {platformsWithGap.map((r) => (
            <option key={r.platform} value={r.platform}>{r.platform} ({r.missingOrders})</option>
          ))}
        </select>
        <label className={cn(secondaryText, smallTextSize, 'flex items-center gap-1')}>
          {t('adminTesting.review.missingFilesBatchSize')}
          <input
            type="number"
            min={1}
            value={batchSize}
            onChange={(e) => setBatchSize(Math.max(1, Number(e.target.value) || 1))}
            className={cn('w-20 px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700', primaryText, smallTextSize)}
          />
        </label>
        <button
          onClick={() => attach(true)}
          disabled={running || stats.missingOrders === 0}
          className={cn('px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50', primaryText, smallTextSize, semiBoldText)}
        >
          {t('adminTesting.review.missingFilesDryRun')}
        </button>
        <button
          onClick={() => attach(false)}
          disabled={running || stats.missingOrders === 0}
          className={cn('px-3 py-1.5 rounded-lg bg-purple-600 dark:bg-purple-600 text-white dark:text-white hover:bg-purple-700 dark:hover:bg-purple-700 disabled:opacity-50', smallTextSize, semiBoldText)}
        >
          {running ? '…' : t('adminTesting.review.missingFilesAttach')}
        </button>
        <span className={cn(secondaryText, 'text-[10px] italic')}>{t('adminTesting.review.missingFilesBatchHint')}</span>
      </div>

      {error && <div className={cn('text-red-500 dark:text-textRed-dark', smallTextSize)}>{error}</div>}

      {platformsWithGap.length === 0 ? (
        <div className={cn(secondaryText, smallTextSize, 'text-center py-8')}>🎉 {t('adminTesting.review.missingFilesEmpty')}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className={cn(secondaryText, 'border-b border-gray-200 dark:border-gray-600')}>
                <th className="text-left py-1 px-2">{t('adminTesting.review.missingFilesColPlatform')}</th>
                <th className="text-right py-1 px-2">{t('adminTesting.review.missingFilesColMissingOrders')}</th>
                <th className="text-right py-1 px-2">{t('adminTesting.review.missingFilesColTotalOrders')}</th>
                <th className="text-right py-1 px-2">{t('adminTesting.review.missingFilesColEmails')}</th>
                <th className="text-right py-1 px-2">{t('adminTesting.review.missingFilesColMissingFacturen')}</th>
              </tr>
            </thead>
            <tbody>
              {platformsWithGap.map((r, i) => (
                <Fragment key={r.platform}>
                  <tr
                    onClick={() => toggleExpanded(r.platform)}
                    className={cn('border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50', i % 2 === 0 ? 'bg-gray-50/50 dark:bg-gray-800/30' : '')}
                  >
                    <td className={cn(primaryText, 'py-1 px-2 font-mono')}>
                      <span className={cn(secondaryText, 'inline-block w-3')}>{expanded === r.platform ? '▼' : '▶'}</span> {r.platform}
                    </td>
                    <td className={cn('py-1 px-2 text-right font-mono', r.missingOrders > 0 ? 'text-red-600 dark:text-red-400' : secondaryText)}>{r.missingOrders}</td>
                    <td className={cn(secondaryText, 'py-1 px-2 text-right font-mono')}>{r.totalOrders}</td>
                    <td className={cn(secondaryText, 'py-1 px-2 text-right font-mono')}>{r.missingEmails}</td>
                    <td className={cn('py-1 px-2 text-right font-mono', r.missingFacturen > 0 ? 'text-red-600 dark:text-red-400' : secondaryText)}>{r.missingFacturen}</td>
                  </tr>
                  {expanded === r.platform && (
                    <tr className="border-b border-gray-100 dark:border-gray-700">
                      <td colSpan={5} className="p-0">
                        <MissingFileEmailList
                          platform={r.platform}
                          startDate={startDate}
                          endDate={endDate}
                          userId={clientId === 'all' ? undefined : clientId}
                          listLimit={Math.max(r.missingEmails, 1)}
                          onRepaired={() => setReloadNonce((n) => n + 1)}
                          t={t}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {run && (
        <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 space-y-2">
          <div className={cn(primaryText, smallTextSize, semiBoldText)}>
            {run.dryRun ? t('adminTesting.review.missingFilesDryRunResult', { orders: run.ordersLinked, facturen: run.facturenLinked, emails: run.emailsConsidered })
                        : t('adminTesting.review.missingFilesRunResult', { orders: run.ordersLinked, facturen: run.facturenLinked, emails: run.emailsConsidered })}
          </div>
          <div className="overflow-x-auto max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className={cn(secondaryText, 'border-b border-gray-200 dark:border-gray-600')}>
                  <th className="text-left py-1 px-2">{t('adminTesting.review.missingFilesColPlatform')}</th>
                  <th className="text-left py-1 px-2">{t('adminTesting.review.missingFilesColOutcome')}</th>
                  <th className="text-right py-1 px-2">{t('adminTesting.review.missingFilesColPdfs')}</th>
                  <th className="text-right py-1 px-2">{t('adminTesting.review.missingFilesColLinked')}</th>
                  <th className="text-left py-1 px-2">{t('adminTesting.review.missingFilesColUnmatched')}</th>
                </tr>
              </thead>
              <tbody>
                {run.emails.map((e, i) => (
                  <tr key={e.gmailMessageId} className={cn('border-b border-gray-100 dark:border-gray-700', i % 2 === 0 ? 'bg-gray-50/50 dark:bg-gray-800/30' : '')}>
                    <td className={cn(primaryText, 'py-1 px-2 font-mono')}>{e.platform}</td>
                    <td className={cn('py-1 px-2', e.outcome === 'linked' ? 'text-green-600 dark:text-green-400' : e.outcome === 'no_pdf_attachment' ? secondaryText : 'text-amber-600 dark:text-amber-400')}>
                      {t(`adminTesting.review.missingFilesOutcome_${e.outcome}`)}
                    </td>
                    <td className={cn(secondaryText, 'py-1 px-2 text-right font-mono')}>{e.pdfCount}</td>
                    <td className={cn(primaryText, 'py-1 px-2 text-right font-mono')}>{e.ordersLinked + e.facturenLinked}</td>
                    <td className={cn(secondaryText, 'py-1 px-2 font-mono text-[10px] max-w-xs truncate')} title={e.unmatchedOrderNumbers.join(', ')}>
                      {e.unmatchedOrderNumbers.join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
